/**
 * @file ReelPresentationConfig.ts
 * @role Validates the single-skin reel presentation contract and cross-checks it against
 *       the authoritative symbol identity registry.
 * @owns Board/cell styling, layered symbol recipes, normalized sockets, and pool sizing hints.
 * @reads Bundled reel-presentation.config.json or injected raw config in tests.
 * @neverDoes Load textures. Render. Decide spin outcomes.
 */
import { z } from 'zod';
import reelPresentationConfigRaw from '@config/reel-presentation.config.json';
import { GameError } from '@core/errors/GameError';
import type { SymbolId } from './SymbolId';

const nonnegative = z.number().nonnegative().max(4_096);
const ratio = z.number().min(0).max(1);
const assetId = z.string().min(1).max(128);
const MAX_SYMBOLS = 128;
const MAX_LAYERS_PER_SYMBOL = 12;
const MAX_SOCKETS_PER_SYMBOL = 16;

function validateSocketMap(
  sockets: Record<string, SymbolSocketConfig>,
  ctx: z.RefinementCtx,
): void {
  const names = Object.keys(sockets);
  if (names.length > MAX_SOCKETS_PER_SYMBOL) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      type: 'array',
      maximum: MAX_SOCKETS_PER_SYMBOL,
      inclusive: true,
      message: `At most ${MAX_SOCKETS_PER_SYMBOL} sockets are allowed`,
    });
  }
  for (const name of names) {
    if (name.length === 0 || name.length > 32) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Socket names must contain 1-32 characters',
        path: [name],
      });
    }
  }
}

const SliceSchema = z.object({
  left: nonnegative,
  top: nonnegative,
  right: nonnegative,
  bottom: nonnegative,
});

const NormalizedPointSchema = z.object({
  x: ratio,
  y: ratio,
});

const DefaultSocketsSchema = z
  .object({
    center: NormalizedPointSchema,
    top: NormalizedPointSchema,
    bottom: NormalizedPointSchema,
    badge: NormalizedPointSchema,
  })
  .catchall(NormalizedPointSchema)
  .superRefine(validateSocketMap);

const SymbolArtLayerSchema = z.object({
  id: z.string().min(1).max(64),
  slot: z.enum(['back', 'main', 'front']),
  textureId: assetId,
  motionTextureId: assetId.optional(),
  sizeRatio: z.number().positive().max(2),
  offset: z
    .object({
      x: z.number().min(-1).max(1),
      y: z.number().min(-1).max(1),
    })
    .default({ x: 0, y: 0 }),
  anchor: NormalizedPointSchema.default({ x: 0.5, y: 0.5 }),
});

const MotionBlurSchema = z
  .object({
    enabled: z.boolean(),
    activateSpeedRatio: ratio,
    deactivateSpeedRatio: ratio,
  })
  .superRefine((value, ctx) => {
    if (value.deactivateSpeedRatio >= value.activateSpeedRatio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'deactivateSpeedRatio must be lower than activateSpeedRatio',
      });
    }
  });

const FlipRevealSchema = z.object({
  faces: z.object({
    bigWin: z.object({ textureId: assetId }),
    freeSpins: z.object({ textureId: assetId }),
  }),
  turnDurationMs: z.number().int().min(100).max(1_500),
  turnPunchScale: z.number().min(1).max(1.3).default(1.06),
  columnDelayMs: z.number().int().min(0).max(1_000),
  elementDelayMs: z.number().int().min(0).max(1_000).default(55),
  holdDurationMs: z.number().int().min(500).max(15_000),
  shakeAmplitudeRatio: z.number().min(0).max(0.25).default(0.04),
  shakeScale: z.number().min(0.7).max(1).default(0.95),
  shakeCycles: z.number().int().min(1).max(40).default(9),
  reducedMotionFadeDurationMs: z.number().int().min(0).max(1_000).default(120),
  reducedMotionHoldDurationMs: z.number().int().min(0).max(2_000).default(500),
  returnDirection: z.enum(['leftToRight', 'rightToLeft']).default('leftToRight'),
  collapseEase: z.enum(['power2.in', 'power3.in', 'sine.in']).default('power3.in'),
  revealEase: z
    .enum(['power2.out', 'power3.out', 'back.out(1.2)', 'back.out(1.35)'])
    .default('back.out(1.35)'),
});

const SymbolPresentationRecipeSchema = z.object({
  layers: z
    .array(SymbolArtLayerSchema)
    .min(1)
    .max(MAX_LAYERS_PER_SYMBOL)
    .superRefine((layers, ctx) => {
      const seen = new Set<string>();
      for (const [index, layer] of layers.entries()) {
        if (seen.has(layer.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate layer id: ${layer.id}`,
            path: [index, 'id'],
          });
        }
        seen.add(layer.id);
      }
    }),
  sockets: z
    .record(z.string().min(1).max(32), NormalizedPointSchema)
    .superRefine(validateSocketMap)
    .optional(),
});

const ReelPresentationBoardSchema = z
  .object({
    frameTextureId: assetId,
    backingColor: z.number().int().min(0).max(0xffffff).default(0),
    backingCornerRadiusRatio: z.number().min(0).max(0.5).default(0.11),
    frameScaleMode: z.enum(['uniform', 'stretch', 'nineSlice']).default('nineSlice'),
    frameNativeSize: z.object({ width: z.number().positive(), height: z.number().positive() }),
    frameContentInsets: SliceSchema,
    contentGap: nonnegative,
    frameSlices: SliceSchema,
    viewportInsets: z.object({
      left: z.number().min(0).max(0.25),
      top: z.number().min(0).max(0.25),
      right: z.number().min(0).max(0.25),
      bottom: z.number().min(0).max(0.25),
    }),
  })
  .superRefine((value, ctx) => {
    const horizontal =
      value.frameContentInsets.left + value.frameContentInsets.right + value.contentGap * 2;
    const vertical =
      value.frameContentInsets.top + value.frameContentInsets.bottom + value.contentGap * 2;
    if (horizontal >= value.frameNativeSize.width || vertical >= value.frameNativeSize.height) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Frame content insets and gap must leave a positive content area',
      });
    }
  });

const ReelPresentationConfigSchema = z.object({
  motionBlur: MotionBlurSchema.default({
    enabled: false,
    activateSpeedRatio: 0.35,
    deactivateSpeedRatio: 0.18,
  }),
  flipReveal: FlipRevealSchema.default({
    faces: {
      bigWin: { textureId: 'ui_reel_flip_big_win_placeholder' },
      freeSpins: { textureId: 'ui_reel_flip_free_spins' },
    },
    turnDurationMs: 180,
    turnPunchScale: 1.06,
    columnDelayMs: 60,
    elementDelayMs: 40,
    holdDurationMs: 1200,
    shakeAmplitudeRatio: 0.04,
    shakeScale: 0.95,
    shakeCycles: 9,
    reducedMotionFadeDurationMs: 120,
    reducedMotionHoldDurationMs: 500,
    returnDirection: 'leftToRight',
    collapseEase: 'power3.in',
    revealEase: 'back.out(1.35)',
  }),
  board: ReelPresentationBoardSchema,
  cell: z.object({
    useBackgroundTexture: z.boolean().default(true),
    backgroundTextureId: assetId,
    backgroundSlices: SliceSchema,
    insetRatio: z.number().min(0).max(0.2),
    fallbackColor: z.number().int().min(0).max(0xffffff),
    fallbackAlpha: ratio,
    cornerRadiusRatio: z.number().min(0).max(0.3),
  }),
  defaultSockets: DefaultSocketsSchema,
  symbols: z
    .record(z.string().min(1).max(64), SymbolPresentationRecipeSchema)
    .superRefine((symbols, ctx) => {
      if (Object.keys(symbols).length > MAX_SYMBOLS) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          type: 'array',
          maximum: MAX_SYMBOLS,
          inclusive: true,
          message: `At most ${MAX_SYMBOLS} symbols are allowed`,
        });
      }
    }),
});

type ParsedConfig = z.infer<typeof ReelPresentationConfigSchema>;

export type ReelPresentationBoardConfig = ParsedConfig['board'];
export type ReelPresentationCellConfig = ParsedConfig['cell'];
export type ReelMotionBlurConfig = ParsedConfig['motionBlur'];
export type ReelFlipRevealConfig = ParsedConfig['flipReveal'];
export type SymbolSocketConfig = z.infer<typeof NormalizedPointSchema>;
export type SymbolArtLayerConfig = z.infer<typeof SymbolArtLayerSchema>;
export type SymbolPresentationRecipe = z.infer<typeof SymbolPresentationRecipeSchema>;
export type SymbolArtSlot = SymbolArtLayerConfig['slot'];
export type LayerCounts = Record<SymbolArtSlot, number>;
export type SymbolSockets = Record<string, SymbolSocketConfig> & {
  center: SymbolSocketConfig;
  top: SymbolSocketConfig;
  bottom: SymbolSocketConfig;
  badge: SymbolSocketConfig;
};

export interface SymbolPresentationBinding {
  readonly recipe: SymbolPresentationRecipe;
  readonly layers: Readonly<Record<SymbolArtSlot, readonly SymbolArtLayerConfig[]>>;
  readonly sockets: SymbolSockets;
}

export class ReelPresentationConfig {
  readonly board: ReelPresentationBoardConfig;
  readonly cell: ReelPresentationCellConfig;
  readonly motionBlur: ReelMotionBlurConfig;
  readonly flipReveal: ReelFlipRevealConfig;
  readonly maxLayerCounts: Readonly<LayerCounts>;

  private readonly defaultSockets: SymbolSockets;
  private readonly bindings: Readonly<Record<SymbolId, SymbolPresentationBinding>>;

  constructor(symbolIds: readonly SymbolId[], raw: unknown = reelPresentationConfigRaw) {
    const parsed = ReelPresentationConfigSchema.safeParse(raw);
    if (!parsed.success) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        `Invalid reel presentation config: ${parsed.error.message}`,
        { cause: parsed.error },
      );
    }

    this.validateSymbolRecipes(symbolIds, parsed.data.symbols);
    this.board = parsed.data.board;
    this.cell = parsed.data.cell;
    this.motionBlur = parsed.data.motionBlur;
    this.flipReveal = parsed.data.flipReveal;
    this.defaultSockets = parsed.data.defaultSockets;
    this.maxLayerCounts = this.calculateMaxLayerCounts(parsed.data.symbols);
    this.bindings = this.compileBindings(parsed.data.symbols);
  }

  requireSymbol(id: SymbolId): SymbolPresentationRecipe {
    return this.requireBinding(id).recipe;
  }

  socketsFor(id: SymbolId): SymbolSockets {
    return this.requireBinding(id).sockets;
  }

  /** Precompiled hot-path binding: merged sockets and per-slot layer arrays allocate once. */
  requireBinding(id: SymbolId): SymbolPresentationBinding {
    const binding = this.bindings[id];
    if (!binding) {
      throw new GameError('CONFIG_LOAD_FAILED', `Missing presentation recipe for symbol: ${id}`);
    }
    return binding;
  }

  /** Asset IDs referenced by this skin's board, cell, and symbol recipes. */
  referencedAssetIds(): readonly string[] {
    const ids = new Set<string>([
      this.board.frameTextureId,
      ...Object.values(this.flipReveal.faces).map((face) => face.textureId),
    ]);
    if (this.cell.useBackgroundTexture) ids.add(this.cell.backgroundTextureId);
    for (const binding of Object.values(this.bindings)) {
      for (const layer of binding.recipe.layers) {
        ids.add(layer.textureId);
        if (layer.motionTextureId) ids.add(layer.motionTextureId);
      }
    }
    return [...ids];
  }

  private validateSymbolRecipes(
    symbolIds: readonly SymbolId[],
    recipes: Record<string, SymbolPresentationRecipe>,
  ): void {
    const expected = new Set(symbolIds);
    const configured = new Set(Object.keys(recipes));
    const missing = [...expected].filter((id) => !configured.has(id));
    const unknown = [...configured].filter((id) => !expected.has(id));
    if (missing.length === 0 && unknown.length === 0) return;

    const details = [
      missing.length > 0 ? `missing: ${missing.join(', ')}` : undefined,
      unknown.length > 0 ? `unknown: ${unknown.join(', ')}` : undefined,
    ].filter((detail): detail is string => detail !== undefined);
    throw new GameError(
      'CONFIG_LOAD_FAILED',
      `Reel presentation symbol recipes do not match the symbol registry (${details.join('; ')})`,
    );
  }

  private calculateMaxLayerCounts(
    recipes: Record<string, SymbolPresentationRecipe>,
  ): Readonly<LayerCounts> {
    const maxima: LayerCounts = { back: 0, main: 0, front: 0 };
    for (const recipe of Object.values(recipes)) {
      const counts: LayerCounts = { back: 0, main: 0, front: 0 };
      for (const layer of recipe.layers) counts[layer.slot] += 1;
      maxima.back = Math.max(maxima.back, counts.back);
      maxima.main = Math.max(maxima.main, counts.main);
      maxima.front = Math.max(maxima.front, counts.front);
    }
    return maxima;
  }

  private compileBindings(
    recipes: Record<string, SymbolPresentationRecipe>,
  ): Readonly<Record<SymbolId, SymbolPresentationBinding>> {
    const bindings: Record<SymbolId, SymbolPresentationBinding> = {};
    for (const [id, recipe] of Object.entries(recipes)) {
      const back: SymbolArtLayerConfig[] = [];
      const main: SymbolArtLayerConfig[] = [];
      const front: SymbolArtLayerConfig[] = [];
      for (const layer of recipe.layers) {
        if (layer.slot === 'back') back.push(layer);
        else if (layer.slot === 'main') main.push(layer);
        else front.push(layer);
      }
      bindings[id] = Object.freeze({
        recipe,
        layers: Object.freeze({
          back: Object.freeze(back),
          main: Object.freeze(main),
          front: Object.freeze(front),
        }),
        sockets: Object.freeze({ ...this.defaultSockets, ...recipe.sockets }),
      });
    }
    return Object.freeze(bindings);
  }
}
